import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import TextInput from "@/components/forms/inputs/TextInput";
import TextArea from "@/components/forms/inputs/TextArea";
import { isDeprecatedSSHKey, isValidSSHKey } from "@/lib/utils";
import { CreateSshKeyInput } from "@/types/savedSshKey";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";

const FIELD_CLASS_NAME =
  "h-[36px] w-full border border-border bg-background text-foreground placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0";

const TEXTAREA_CLASS_NAME =
  "min-h-[100px] w-full border border-border bg-background font-mono text-sm text-foreground placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0";

type AddSshKeyDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: CreateSshKeyInput) => Promise<void>;
};

export const AddSshKeyDialog: React.FC<AddSshKeyDialogProps> = ({
  isOpen,
  onOpenChange,
  onSubmit
}) => {
  const [name, setName] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setName("");
    setPublicKey("");
    setError(null);
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const trimmedKey = publicKey.trim();
    if (!trimmedName) {
      setError("Key name is required");
      return;
    }
    if (trimmedName.length > 16) {
      setError("Key name too long");
      return;
    }
    if (!trimmedKey) {
      setError("Public key is required");
      return;
    }
    if (!isValidSSHKey(trimmedKey)) {
      setError("Invalid SSH key format");
      return;
    }
    if (isDeprecatedSSHKey(trimmedKey)) {
      setError("DSA keys are not supported for security reasons");
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({ name: trimmedName, publicKey: trimmedKey });
      trackEvent(AnalyticsEventType.SAVED_SSH_KEY_ADDED, {});
      reset();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save SSH key");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNameError =
    Boolean(error) &&
    (/name/i.test(error ?? "") || error === "Key name is required" || error === "Key name too long");
  const isPublicKeyError = Boolean(error) && !isNameError;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) reset();
        onOpenChange(open);
      }}
    >
      <DialogContent className="w-full max-w-lg gap-0 px-0 py-0" showCloseIcon={false}>
        <DialogHeader className="px-8 pt-4">
          <DialogTitle className="title-h4 text-foreground">Add SSH key</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 px-8 py-4">
          <div className="flex flex-col gap-2">
            <label className="label-small text-foreground" htmlFor="ssh-key-name">
              Key name <span className="text-blue-light-300">*</span>
            </label>
            <TextInput
              id="ssh-key-name"
              name="ssh-key-name"
              value={name}
              onChange={setName}
              placeholder="my laptop"
              maxLength={16}
              className={FIELD_CLASS_NAME}
              error={isNameError}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="label-small text-foreground" htmlFor="ssh-key-value">
              Public key <span className="text-blue-light-300">*</span>
            </label>
            <TextArea
              id="ssh-key-value"
              name="ssh-key-value"
              value={publicKey}
              onChange={setPublicKey}
              placeholder="ssh-ed25519 AAAA..."
              rows={4}
              className={TEXTAREA_CLASS_NAME}
              error={isPublicKeyError}
            />
          </div>
          {error ? <p className="text-sm text-red-dark-300">{error}</p> : null}
        </div>
        <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg border-t px-4 py-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={() => void handleSubmit()} disabled={isSubmitting}>
            Save key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
